'use client'
import React, { createContext, useState, useEffect, useContext } from 'react';
import Bugsnag from '@bugsnag/js';
import { User, Installer, Role } from '@/app/types/global';
import { usePathname } from 'next/navigation';
import { cachedFetchUser } from '@/app/utils/utils';
import {
  currentRoute,
  consumeLoginNext,
  fetchUserWithRetry,
  isExpiredSessionError,
  loginRedirectFor,
} from '@/app/utils/authRedirect';
import {
  allowsAnonymousContext,
  isProviderlessPublicPath,
  shouldFetchCurrentUser,
} from '@/app/utils/authPageAccess';

type UserContextType = {
  user: User | null;
  installer: Installer | null;
  showStatus: boolean;
  setShowStatus: React.Dispatch<React.SetStateAction<boolean>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setInstaller: React.Dispatch<React.SetStateAction<Installer | null>>;
  isInstaller: boolean;
  isAdmin: boolean;
  isHacker: boolean;
  isActive: boolean;
  isNone: boolean;
  isAnonymous: boolean;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathName = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [installer, setInstaller] = useState<Installer | null>(null);
  const [redirected, setRedirected] = useState(false);
  const [sessionFailure, setSessionFailure] = useState(false);
  const [sessionRetry, setSessionRetry] = useState(0);
  const isInstaller = !!user?.role && ['installer', 'admin'].includes(user.role);
  const [showStatus, setShowStatus] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('showStatus');
    return stored !== null ? JSON.parse(stored) : false;
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 25;
    const stored = localStorage.getItem('pageSize');
    return stored !== null ? JSON.parse(stored) : 25;
  });
  
  useEffect(() => {
    localStorage.setItem('showStatus', JSON.stringify(showStatus));
  }, [showStatus]);

  // Attach the current user to any future Bugsnag reports so we can see
  // who was affected when something goes wrong. setUser is silent — it
  // doesn't generate a report itself, only adds context to future ones.
  useEffect(() => {
    try {
      if (user) {
        Bugsnag.setUser(String(user.id), user.email, user.name);
      } else {
        Bugsnag.setUser(undefined, undefined, undefined);
      }
    } catch {
      // Bugsnag not started (e.g. in dev, or before init) — safe to ignore
    }
  }, [user]);

  const isAnonymous = !user;
  const isHacker = !!user?.role && ['installer', 'hacker', 'admin'].includes(user.role);
  const isAdmin = !!user?.role && ['admin'].includes(user.role);
  const isNone = !!user?.role && ['none'].includes(user.role);
  const isActive = !!user?.subscription_status && ['active'].includes(user.subscription_status);

  const isPublicPage = isProviderlessPublicPath(pathName);
  const isAnonymousContextPage = allowsAnonymousContext(pathName);
  useEffect(() => {
    let active = true;
    const fetchUser = async () => {
      setSessionFailure(false);
      try {
        const data = await fetchUserWithRetry(cachedFetchUser);
        if (!active) return;
        if (data) {
          if (data?.email) {
            setUser(data as User);
            if (typeof window !== 'undefined' && window.location.pathname === '/') {
              const pendingNext = consumeLoginNext();
              if (pendingNext && pendingNext !== currentRoute(window.location)) {
                window.location.href = pendingNext;
                return;
              }
            }
            const installerResponse = await fetch('/api/installer');
            if (installerResponse.ok) {
              setInstaller(await installerResponse.json());
            }
          } else {

            if (process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
              setRedirected(true);
              window.location.href = loginRedirectFor(currentRoute(window.location));
            }
          }
        } else {
          if (process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
            setRedirected(true);
            window.location.href = loginRedirectFor(currentRoute(window.location));
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        if (!active) return;
        if (isExpiredSessionError(error) && process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
          setRedirected(true);
          window.location.href = loginRedirectFor(currentRoute(window.location));
        } else {
          setSessionFailure(true);
        }
      }
    };
    
    if (shouldFetchCurrentUser(pathName) && !user && !redirected) {
      fetchUser();
    }
    return () => { active = false; };
  }, [pathName, redirected, sessionRetry, user]);

  if (isPublicPage) {
    return <>{children}</>;
  }

  // Allow anonymous render for public-ish pages
  if (isAnonymousContextPage) {
    return (
      <UserContext.Provider value={{
        user, installer,
        pageSize, setPageSize,
        showStatus, setShowStatus,
        setUser, setInstaller,
        isInstaller, isAdmin, isHacker, isActive, isNone, isAnonymous
      }}>
        {children}
      </UserContext.Provider>
    );
  }
  
  if (sessionFailure && !user) {
    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <section
          role="alert"
          aria-labelledby="session-status-heading"
          className="w-full max-w-lg rounded-lg border border-amber-300 dark:border-amber-700 bg-card p-6 shadow-sm"
        >
          <h1 id="session-status-heading" className="text-xl font-semibold">We could not check your session</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Powston is temporarily unreachable. You have not been signed out, and your requested page is still saved.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setSessionRetry(value => value + 1)}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Try again
            </button>
            <a className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted" href="/system-status">
              View system status
            </a>
          </div>
        </section>
      </main>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <UserContext.Provider value={{
      user, installer,
      pageSize, setPageSize,
      showStatus, setShowStatus,
      setUser, setInstaller,
      isInstaller, isAdmin, isHacker, isActive, isNone, isAnonymous
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * Soft variant of useUser for components that may render on public pages
 * (login, signup, passkey) where UserProvider intentionally renders children
 * without a UserContext.Provider. Returns null instead of throwing so
 * layout-level UI (banners, etc.) can short-circuit gracefully.
 */
export const useOptionalUser = (): UserContextType | null => {
  const context = useContext(UserContext);
  return context ?? null;
};

/**
 * Phase 0 of doc/guidelines.md: collapse the overlapping booleans
 * (isInstaller, isAdmin, isHacker) into a single canonical role per
 * the three-role model. Precedence: admin > installer > customer.
 *
 * - 'hacker' is treated as 'installer' — hackers see device-level
 *   internals (Modbus, firmware) that installers need but admins
 *   exclusively see (algorithm internals, ROI, etc.) stay hidden.
 * - Anonymous users default to 'customer' so role-gated UI fails
 *   closed (hides installer/admin info) rather than open.
 */
export const useRole = (): Role => {
  const context = useContext(UserContext);
  if (!context) return 'customer';
  if (context.isAdmin) return 'admin';
  if (context.isInstaller || context.isHacker) return 'installer';
  return 'customer';
};
